/**
 * turnstile.ts — client-side Turnstile verification utility.
 *
 * Single responsibility: take a token produced by useTurnstile and POST it to
 * our own /api/auth/verify-turnstile for server-side validation.
 *
 * This function is the Phase 2 integration point — the Boarding Pass
 * Clearance™ UI calls this same function after its branded challenge flow.
 * Do not change this interface; change only the UI layer above it.
 */

const VERIFY_ENDPOINT = '/api/auth/verify-turnstile';

export class TurnstileVerificationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TurnstileVerificationError';
  }
}

/**
 * Verify a Turnstile token with our API server.
 * Throws TurnstileVerificationError on failure so callers can catch and
 * display an appropriate message without leaking internal details.
 */
export async function verifyTurnstileToken(token: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch(VERIFY_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
      credentials: 'include',
      // REDIRECT FIX: if the server sends any redirect (e.g. 301 www→apex),
      // throw immediately instead of silently following it and converting the
      // POST to a GET. The GET has no handler → 404 → silent wrong response.
      // Callers handle the thrown TypeError the same as any network error.
      redirect: 'error',
    });
  } catch {
    // Network failure or redirect (redirect:'error') — fail open in dev,
    // hard-fail in production.
    if (import.meta.env.DEV) {
      console.warn('[Turnstile] Network error reaching verify endpoint — bypassing in dev mode');
      return;
    }
    throw new TurnstileVerificationError(
      'Security check failed. Please check your connection and try again.',
    );
  }

  if (res.ok) return;

  if (res.status === 400) {
    throw new TurnstileVerificationError(
      'Security check failed. Please refresh the page and try again.',
    );
  }

  // 5xx or unexpected — fail open in dev, hard-fail in prod.
  if (import.meta.env.DEV) {
    console.warn(`[Turnstile] Verify endpoint returned ${res.status} — bypassing in dev mode`);
    return;
  }

  throw new TurnstileVerificationError(
    'Security verification unavailable. Please try again in a moment.',
  );
}
